import { Button } from "../components/ui/button";
import { FilePdf, BookOpen } from "@phosphor-icons/react";

const SECTIONS = [
  {
    title: "1. Getting Started",
    content: `Log in with your admin credentials. You'll land on the Floor Plan. The app has three roles: Admin (full access), Host/Hostess (operational tabs), and Server (restricted to their section + chat).`,
  },
  {
    title: "2. Service Bar (Top Bar)",
    content: `The bar at the top controls your daily lifecycle:\n\n• No Active Service → Click "Begin Pre-Service" to start setup\n• Pre-Service Setup → Run the checklist, assign sections, review reservations, then click "Start Service"\n• Service Active → Manage tables, seat guests, handle walk-ins. Click "End Service" when done\n• Post-Service → Enter sales by category, view the recap summary, export a Daily Recap PDF, then "Reset for Next Day"`,
  },
  {
    title: "3. Floor Plan",
    content: `Your visual restaurant layout.\n\n• Add Tables: Click "+ Add Table" at the bottom. Choose presets or custom sizes.\n• Drag & Drop: Reposition tables by dragging them.\n• Resize & Rotate: Select a table, use corner handles to resize and the slider to rotate.\n• Multi-Floor: Switch between floors using tabs. On iPad, floor tabs are at the bottom.\n• Section Glows: Tables glow with their section color. The Sections legend (bottom-left) shows server = color.\n• Quick Notes: Orange pencil badge on tables with notes. Hover to see the note.\n• Status Auto-Sync: Changing a table from Reserved → Occupied automatically seats the reservation. Occupied → Available completes it.\n• Save/Load Layouts: Save your floor arrangement as a template for quick setup.`,
  },
  {
    title: "4. Waitlist",
    content: `Manage guests waiting for a table.\n\n• Click "+ Add Guest" with name, phone, and party size\n• Each guest gets a QR code — when scanned, shows their live wait status on a public page (no login needed)\n• Change status to "Notified" when table is almost ready, or "Seated" when placed`,
  },
  {
    title: "5. Reservations",
    content: `Manage all reservations by date.\n\n• Click "+ New Reservation" — enter guest name, phone, member number, party size, date, time, table, and notes\n• Times are in 12-hour format (e.g., "7:00 PM")\n• Action buttons: Seat → Complete → or Reopen if completed by accident\n• Cancel or Delete reservations as needed\n• Drag reservations onto floor plan tables to seat them visually`,
  },
  {
    title: "6. Walk-Ins",
    content: `Same as Reservations but for walk-in guests. Current time auto-fills. Walk-ins are tracked separately in analytics for reservation vs walk-in ratios.`,
  },
  {
    title: "7. Servers",
    content: `Manage staff and daily scheduling.\n\n• Staff Pool: All registered servers with assigned colors\n• Weekly Calendar: See who's scheduled each day\n• Copy: Duplicate one day's schedule to another\n• Rotate: Auto-rotate section assignments\n• Servers who register with the "Server" role are auto-added to the pool`,
  },
  {
    title: "8. Sections",
    content: `Organize tables into server sections.\n\n• Create sections with a name, color, and assigned server\n• Go to Floor Plan → "Assign Tables to Sections" → click a section, then click tables\n• Each section gets a unique color glow on the floor plan\n• The Sections legend shows which server owns which section`,
  },
  {
    title: "9. Events",
    content: `Plan special events (private dinners, corporate, weddings, birthdays, etc.).\n\n• Set event name, type, date, time, guest count, contact info, assigned tables\n• Track status: Planned → Confirmed → In Progress → Completed`,
  },
  {
    title: "10. Analytics",
    content: `Two views:\n\nTODAY: Live metrics — covers, occupancy, avg turn time, hourly breakdown, server load, wait estimate, server leaderboard.\n\nHISTORICAL: Multi-day trends — covers/day, reservations vs walk-ins, avg turn time, service duration, guest insights (returning vs new), server rankings, and a Peak Hours Heatmap showing your busiest day/hour combinations.\n\nPeriod selector: 7d / 14d / 30d`,
  },
  {
    title: "11. Chat",
    content: `Two-way messaging between hosts and servers.\n\n• Real-time via WebSocket — messages appear instantly\n• Quick presets: "Table needs bussing", "Ready for next seating", "Need manager"\n• Push notifications when messages arrive (if browser notifications enabled)`,
  },
  {
    title: "12. Notifications",
    content: `System notification feed showing reservation activity, table assignments, service events.\n\n• "Announce" button sends a custom message to all staff\n• Push notifications supported via browser Service Worker`,
  },
  {
    title: "13. Server Stats (Admin)",
    content: `Track individual server performance.\n\n• Summary: Total covers, turns, active servers\n• Per-server cards: Covers, turns, avg turn time, table count\n• History button: View daily breakdown per server\n• Stats persist after daily reset (saved to permanent history)`,
  },
  {
    title: "14. Members (Admin)",
    content: `CRM with tier-based loyalty system.\n\nLayout:\n• Top Member Spotlight — highest visit count\n• Gold (5+ visits/7d), Silver (3+), Bronze (2+)\n• All Members in 2-column grid\n\nClick a member for detailed analytics: visit counts (7d/30d), tier progress bar, allergens, special requests, and recent reservations.\n\nMembers auto-save from reservations/walk-ins at end of service. Visit counts deduplicated per day.\n\nExport: CSV + PDF with tier data.`,
  },
  {
    title: "15. Sales (Admin)",
    content: `Category-based sales tracking with subtotals.\n\nFood Sales: Brunch, Lunch, Dinner, Kids Menu, Employees Menu, Turn Menu, Side Orders, Dessert, Bev Cart F&B\nWine Sales: Wine by the Glass (White/Red), Wine Bottles\nLiquor Sales: Rum & Tequila, Vodka & Gins, Bourbon, Whiskey & Scotch, Bev Cart Liquor, Mixed Drinks\nBeer Sales: Beer, Bev Cart Beer\nOther: Soft Drinks, Events\n\nSubtotals auto-calculate. Grand total = all subtotals combined.\n\nCharts: Sales trend, category breakdown donut, sales vs covers.\nEnd-of-service prompt: Enter sales by category when you end service.`,
  },
  {
    title: "16. Service History (Admin)",
    content: `Historical end-of-service snapshots by date.\n\nFor each day: Total covers, reservations, avg turn, service duration, member analytics (unique/returning/new + tier breakdown), server analytics (ranked leaderboard), table analytics.`,
  },
  {
    title: "17. Data & Logistics (Admin)",
    content: `Export reports and view operational data.\n\n• Export PDF: Daily Report, Members, Server Performance\n• Reservation History: Chart of reservations over 7/14/30 days\n• Service Session History: Past sessions with times, covers, reservation counts`,
  },
  {
    title: "18. Settings (Admin)",
    content: `Configure your restaurant.\n\n• Restaurant Info: Name, address, phone, timezone\n• Service Hours: Opening/closing times, default turn time\n• Staff Accounts: View all accounts, change roles, lock/unlock`,
  },
  {
    title: "19. Daily Workflow",
    content: `BEFORE SERVICE:\n1. Begin Pre-Service → run checklist\n2. Review reservations, assign sections, schedule servers\n\nDURING SERVICE:\n3. Start Service → manage tables, seat guests, handle walk-ins\n4. Monitor analytics, communicate via chat\n\nAFTER SERVICE:\n5. End Service → enter sales by category\n6. Review summary → export Daily Recap PDF\n7. Reset for Next Day → clears tables, reservations, sections, chat. Members + server stats saved permanently.`,
  },
  {
    title: "20. iPad Tips",
    content: `• Floor tabs move to bottom bar for max canvas space\n• Pinch to zoom, drag to pan the floor plan\n• All buttons are touch-friendly\n• Sidebar collapses to bottom nav on small screens`,
  },
];

export default function WalkthroughPage() {
  const printPDF = () => {
    const html = `<html><head><title>Dining Floor Studio — Admin Walkthrough</title>
      <style>
        body { font-family: 'Helvetica Neue', Arial, sans-serif; max-width: 750px; margin: 30px auto; color: #1C1917; padding: 20px; font-size: 13px; line-height: 1.6; }
        h1 { font-size: 26px; color: #2F4F4F; margin-bottom: 4px; }
        .subtitle { color: #78716C; font-size: 13px; margin-bottom: 30px; }
        h2 { font-size: 16px; color: #2F4F4F; margin-top: 28px; margin-bottom: 8px; border-bottom: 1px solid #E7E5E4; padding-bottom: 4px; }
        p { white-space: pre-line; margin: 0 0 12px 0; }
        .footer { margin-top: 40px; text-align: center; font-size: 10px; color: #A8A29E; }
        @media print { body { margin: 15px; } h2 { page-break-after: avoid; } }
      </style></head><body>
      <h1>Dining Floor Studio</h1>
      <p class="subtitle">Admin Walkthrough — Complete Feature Guide</p>
      ${SECTIONS.map((s) => `<h2>${s.title}</h2><p>${s.content}</p>`).join("")}
      <div class="footer">Dining Floor Studio — Created by Jordan Thomas</div>
      </body></html>`;
    const win = window.open("", "_blank");
    win.document.write(html);
    win.document.close();
    setTimeout(() => { win.print(); }, 500);
  };

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto" data-testid="walkthrough-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <BookOpen size={24} weight="duotone" style={{ color: "#2F4F4F" }} />
          <h2 className="text-xl sm:text-2xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans", color: "#1C1917" }}>Admin Walkthrough</h2>
        </div>
        <Button onClick={printPDF} variant="outline" size="sm" className="text-xs" data-testid="print-walkthrough-btn">
          <FilePdf size={14} className="mr-1" /> Save as PDF
        </Button>
      </div>

      <div className="space-y-6">
        {SECTIONS.map((s, i) => (
          <div key={i} className="bg-white rounded-lg border border-stone-200 p-5" data-testid={`section-${i}`}>
            <h3 className="text-sm font-bold text-stone-900 mb-2" style={{ fontFamily: "Work Sans", color: "#2F4F4F" }}>{s.title}</h3>
            <p className="text-sm text-stone-600 whitespace-pre-line leading-relaxed">{s.content}</p>
          </div>
        ))}
      </div>

      <p className="text-center text-xs text-stone-400 mt-8 mb-4">Dining Floor Studio — Created by Jordan Thomas</p>
    </div>
  );
}
